import {
  CellDoubleClickedEvent,
  GetContextMenuItemsParams,
  GridOptions,
  MenuItemDef,
} from '@ag-grid-community/core'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  enableHeliosMultiRowDragSelection,
} from '@helios/ag-grid-config'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { AccountDetailsKey } from '@helios/panels/account-details'
import {
  DensityLevel,
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import {
  AccountNotes,
  AccountNotesModel,
} from '../../models/account-details.model'
import { AccountDetailsService } from '../../services/account-details.service'
import { NoteModalComponent } from '../note-modal/note-modal.component'

@Component({
  selector: 'hs-account-notes-grid',
  templateUrl: './account-notes-grid.component.html',
  styleUrls: ['./account-notes-grid.component.scss'],
})
export class AccountNotesGridComponent implements OnDestroy {
  /**
   *  loads the account selected
   *
   * @param accountIds account ids
   */
  @Input()
  set accountListSelectionChanged(accountIds: number[]) {
    if (accountIds.length > 0) this.loadAccountNotes(accountIds)
  }
  /**
   * DO NOT CHANGE. Saved Views and Filter Groups depend on this key. Changing will result in users losing their saved
   * preferences for this grid.
   */
  AccountNotesMainGridKey = `${AccountDetailsKey}-accountNotesGrid`
  rowAccountNotesData: AccountNotesModel = {
    notes: [],
  }
  accountIds: number[] = []
  loading = false
  private readonly accountNotesField = composeFieldPath(
    this.rowAccountNotesData?.notes,
  )

  private readonly destroySubject$ = new Subject<void>()

  gridOptionsAccountNotes: GridOptions = {
    sideBar: false,
    enableBrowserTooltips: true,
    columnDefs: [
      {
        field: this.accountNotesField('accountID'),
        headerName: 'Account ID',
        width: 300,
      },
      {
        field: this.accountNotesField('note'),
        headerName: 'Note',
        width: 800,
        tooltipComponentParams: {
          showTooltip: () => true,
        },
      },
    ],
    rowSelection: 'multiple',

    getContextMenuItems: params => this.getContextMenuItems(params),
    onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
      this.editNote(event.data)
    },
    ...disableChangeDetectionOnScroll(this.changeDetector),
    onGridReady: params => {
      this.savedViewService.manageSavedView(
        params,
        this.AccountNotesMainGridKey,
        this.destroySubject$,
      )
      this.preferences
        .getDensityLevel()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(level =>
          enableHeliosGridDensityLevel(
            params.api,
            DensityLevel.Compact === level,
          ),
        )
      enableHeliosCopyBehavior(params.api)
      enableHeliosMultiRowDragSelection(params.api)
    },
  }

  constructor(
    public accountDetailsService: AccountDetailsService,
    private readonly modalService: NgbModal,
    public toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  getContextMenuItems(params: GetContextMenuItemsParams) {
    let notesSelected: AccountNotes[]
    if (!params?.node?.id) {
      return []
    }

    if (params.api.getSelectedNodes().length > 1) {
      notesSelected = params.api.getSelectedNodes().map(t => t.data) ?? []
    } else {
      notesSelected = [params.node.data] as AccountNotes[]
    }

    const result: (string | MenuItemDef)[] = [
      {
        name: 'Edit Note',
        icon: '<span class="mdi mdi-pencil"></span>',
        action: () => {
          this.editNote(notesSelected)
        },
      },
    ]

    return result
  }

  async onAddNote() {
    await this.addNote()
  }

  async addNote() {
    const modalRef = this.modalService.open(NoteModalComponent, {
      keyboard: false,
    } as NgbModalOptions)
    const modalComponent = modalRef.componentInstance as NoteModalComponent
    if (this.accountIds == undefined || this.accountIds.length == 0) {
      throw new Error('Cannot add note without account id')
    }
    modalComponent.modalTitle = 'Add Note'
    let note: string
    try {
      note = await modalRef.result
    } catch (error) {
      return // modal dismissed, just return
    }
    await this.accountDetailsService.addNotes(note, this.accountIds).toPromise()
    await this.loadAccountNotes(this.accountIds)
  }

  async editNote(notesSelected: AccountNotes[]) {
    const modalRef = this.modalService.open(NoteModalComponent, {
      keyboard: false,
    } as NgbModalOptions)
    const modalComponent = modalRef.componentInstance as NoteModalComponent
    if (notesSelected.length == 1) modalComponent.note = notesSelected[0].note
    modalComponent.modalTitle = 'Edit Note'

    let newNote: string
    try {
      newNote = await modalRef.result
    } catch (error) {
      return // modal dismissed, just return
    }
    await this.accountDetailsService
      .updateNotes(
        notesSelected.flatMap(note => note.noteID),
        newNote,
      )
      .toPromise()
    await this.loadAccountNotes(this.accountIds)
  }

  async loadAccountNotes(accountIds: number[]) {
    this.loading = true
    this.accountIds = accountIds
    const accountNotesDetails = await this.accountDetailsService
      .getAccountNotesV2(accountIds)
      .toPromise()
    if (accountNotesDetails != null) {
      const rowAccountNotesData = Object.values(
        accountNotesDetails.notes,
      ).flat()
      this.rowAccountNotesData.notes = rowAccountNotesData
    }
    this.loading = false
  }
}
